import { AfterViewInit, Component, computed, DestroyRef, inject, ElementRef, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Subscription } from "rxjs";
import ScheduleDetailHeader from "../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { db, ImageType, InspectionJob, JobIssue, Lot, Media, SyncableStatus } from "../../../../core/database/db";
import { SkeletonModule } from "primeng/skeleton";
import { LoggerService } from "../../../../shared/services/logger.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { ScoreCardListComponent } from "./components/score-card-list/score-card-list.component";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { CmLotService } from "../all-my-lots/services/cm-lot.service";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { ExtendedRouter } from "../../../../shared/services/extended-router.service";
import { AddNewItemBottomSheetComponent } from "../open-items/open-items-list/components/add-new-item/add-new-item-bottom-sheet.component";
import { DateUtilsService } from "../../../../shared/services/date.service";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";
import { DueFilterValue } from "../open-items/open-items-list/services/filter.service";
import SyncManager from "../../../../core/sync/sync-manager";
import { BlobService } from "../../../../shared/services/blob.service";
import { MediaService } from "../../../../domains/media/services/media.service";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../domains/media/services/image-compression-pipeline.service";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import { ImageHydrationService } from "../../../../core/sync/image-sync/image-hydration.service";
import { FeatureService } from "../../../../core/feature-flags/feature.service";
import { AdminConfigKey, LotPrimaryIdentifier } from "../../../../core/feature-flags/models/AdminConfigDto";

export interface LotActionSummary {
  openItemsCount: number;
  overdueItemsCount: number;
  /** Days the oldest overdue item has been past due, or null when nothing is overdue. */
  oldestOverdueDays: number | null;
  /** Soonest due date across open items, including ones already overdue. */
  nextDueDate: Date | null;
}

@Component({
  selector: "app-job-summary",
  standalone: true,
  imports: [CommonModule, ScheduleDetailHeader, SkeletonModule, ScoreCardListComponent, BottomSheetComponent, VerticalButtonGroupComponent, AddNewItemBottomSheetComponent, DataUpdatedFadeDirective, BackgroundRefreshIndicatorComponent],
  templateUrl: "./lot-summary.component.html",
  styleUrl: "./lot-summary.component.scss"
})
export class LotSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
  title = "Summary";

  lotId: number = 0;
  communityId: number = 0;
  fromAllLots: boolean = false;

  lot: Lot | undefined;
  jobs: InspectionJob[] = [];
  actionSummary: LotActionSummary = {
    openItemsCount: 0,
    overdueItemsCount: 0,
    oldestOverdueDays: null,
    nextDueDate: null
  };

  dataLoaded: boolean = false;

  lotPrimaryIdentifier: LotPrimaryIdentifier = LotPrimaryIdentifier.LotBlock;

  isLotMenuOpen: boolean = false;
  isAddNewItemOpen: boolean = false;
  isLotPhotoMenuOpen: boolean = false;
  currentLotPhoto = signal<Media | undefined>(undefined);
  lotPhotoUrl = signal<SafeUrl | string | undefined>(undefined);
  private lotPhotoObjectUrl: string | undefined;
  private backgroundRefreshCoordinator?: BackgroundRefreshCoordinator;
  readonly isRefreshingInBackground = computed(() => this.constructionManagerScheduleService.isFetchingSchedule());
  readonly dataUpdatedPulse = computed(() => this.backgroundRefreshCoordinator?.dataUpdatedPulse() ?? false);

  showAllSteps: boolean = true;
  isScrolled: boolean = false;
  /** Measured at runtime so the steps header pins below a lot header of any height. */
  lotHeaderHeight: number = 0;

  @ViewChild("lotHeaderLayer") private lotHeaderLayer?: ElementRef<HTMLElement>;
  @ViewChild("lotPhotoCameraInput") private lotPhotoCameraInput?: ElementRef<HTMLInputElement>;
  @ViewChild("lotPhotoLibraryInput") private lotPhotoLibraryInput?: ElementRef<HTMLInputElement>;
  private lotHeaderResizeObserver?: ResizeObserver;
  private syncCompletedSubscription?: Subscription;

  constructor(
    private router: ExtendedRouter,
    private route: ActivatedRoute,
    private logger: LoggerService,
    private toastService: UiToastService,
    private lotService: LotService,
    private jobService: JobService,
    private jobIssueService: JobIssueService,
    private location: Location,
    private cmLotService: CmLotService,
    private dateUtils: DateUtilsService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private syncManager: SyncManager,
    private blobService: BlobService,
    private mediaService: MediaService,
    private sanitizer: DomSanitizer,
    private imageCompressionPipelineService: ImageCompressionPipelineService,
    private imageHydrationService: ImageHydrationService,
    private featureService: FeatureService
  ) { }

  private readonly destroyRef = inject(DestroyRef);

  async ngOnInit(): Promise<void> {
    this.lotId = parseInt(this.route.snapshot.paramMap.get("lotId") ?? "0");
    this.communityId = parseInt(this.route.snapshot.paramMap.get("communityId") ?? "0");
    this.fromAllLots = (this.location.getState() as { fromAllLots?: boolean })?.fromAllLots ?? false;

    if (this.lotId === 0) {
      this.alertErrorAndNavigateBack("Invalid lot ID");
      return;
    }

    this.lotPrimaryIdentifier = (await this.featureService.getEffectiveConfig(AdminConfigKey.LotPrimaryIdentifier)) ?? LotPrimaryIdentifier.LotBlock;

    await this.loadLotData();

    // Subscribes to the CM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    this.backgroundRefreshCoordinator = new BackgroundRefreshCoordinator(
      this.constructionManagerScheduleService.scheduleRefreshed,
      async redirectOnMissingData => {
        const isBackgroundRefresh = !redirectOnMissingData;
        await this.loadLotJobsData(true, isBackgroundRefresh);
        await this.loadLotIssueData(isBackgroundRefresh);
        await this.loadLotPhoto();
        return true;
      },
      this.destroyRef
    );

    await this.backgroundRefreshCoordinator.runInitialLoad();

    this.dataLoaded = true;

    this.syncCompletedSubscription = this.syncManager.onSyncCompleted.subscribe(() => void this.refreshLotData());
  }

  get isAddressPrimary(): boolean {
    return this.lotPrimaryIdentifier === LotPrimaryIdentifier.Address;
  }

  get primaryDisplayValue(): string {
    return this.isAddressPrimary ? (this.lot?.streetAddress ?? "") : `Lot ${this.lot?.lotBlock ?? ""}`;
  }

  get secondaryDisplayValue(): string {
    return this.isAddressPrimary ? `Lot ${this.lot?.lotBlock ?? ""}` : (this.lot?.streetAddress ?? "");
  }

  /** A background sync can add, complete or re-date issues while this page is open. */
  private async refreshLotData(): Promise<void> {
    await this.loadLotJobsData(false, true);
    await this.loadLotIssueData();
    await this.loadLotPhoto();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    this.lotHeaderResizeObserver = new ResizeObserver(() => this.measureLotHeader());
    if (this.lotHeaderLayer) {
      this.lotHeaderResizeObserver.observe(this.lotHeaderLayer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.lotHeaderResizeObserver?.disconnect();
    this.syncCompletedSubscription?.unsubscribe();
    if (this.lotPhotoObjectUrl) {
      URL.revokeObjectURL(this.lotPhotoObjectUrl);
    }
  }

  onContentScroll(event: Event): void {
    this.isScrolled = (event.target as HTMLElement).scrollTop > 0;
  }

  toggleShowAllSteps(): void {
    this.showAllSteps = !this.showAllSteps;
  }

  openLotMenu(): void {
    this.isLotMenuOpen = true;
  }

  get lotMenuButtons(): VerticalButton[] {
    return [
      {
        label: "Add New Item",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.openAddNewItem(),
        testId: "cm-btn-add-new-item"
      },
      {
        label: "Completed Items",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.viewCompletedItems(),
        testId: "btn-completed-items"
      },
      {
        label: "Archive Lot",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => void this.archiveLot(),
        testId: "cm-btn-lot-archive-sheet"
      }
    ];
  }

  openAddNewItem(): void {
    this.isLotMenuOpen = false;
    this.isAddNewItemOpen = true;
  }

  onItemAdded(issue: JobIssue): void {
    this.toastService.successWithAction("Item Added Successfully", {
      label: "View",
      onClick: () => this.viewOpenItemsWithHighlight(issue.id!)
    });
    void this.loadLotIssueData();
  }

  private viewOpenItemsWithHighlight(issueId: number): void {
    this.router.navigateWithPersistedState(
      ["cm", "all-my-lots", "community", this.communityId, "lot", this.lotId, "open-items", "list"],
      { state: { highlightIssueId: issueId } }
    );
  }

  async archiveLot(): Promise<void> {
    this.isLotMenuOpen = false;
    if (!this.lotId) {
      return;
    }
    const success = await this.cmLotService.archiveLot(this.lotId);
    if (success) {
      this.goBack();
    }
  }

  private measureLotHeader(): void {
    this.lotHeaderHeight = this.lotHeaderLayer?.nativeElement.offsetHeight ?? 0;
  }

  private async loadLotData(): Promise<void> {
    try {
      this.lot = await this.lotService.getLotById(this.lotId);

      if (!!this.lot === false) {
        this.alertErrorAndNavigateBack(`Lot with id ${this.lotId} not found`);
        return;
      }

      await this.loadLotPhoto();
    } catch (error) {
      this.logger.error("Error loading lot data", error);
      this.alertErrorAndNavigateBack("Error loading lot data");
    }
  }

  private async loadLotPhoto(): Promise<void> {
    const media = await this.mediaService.getActiveMediaForParent(String(this.lotId));
    this.currentLotPhoto.set(media.find(m => m.imageType === ImageType.LotPhoto));
    this.updateLotPhotoUrl();
  }

  private updateLotPhotoUrl(): void {
    if (this.lotPhotoObjectUrl) {
      URL.revokeObjectURL(this.lotPhotoObjectUrl);
      this.lotPhotoObjectUrl = undefined;
    }

    const currentLotPhoto = this.currentLotPhoto();
    if (!currentLotPhoto) {
      this.lotPhotoUrl.set(undefined);
      return;
    }

    if (currentLotPhoto.base64Image) {
      this.lotPhotoObjectUrl = URL.createObjectURL(this.blobService.base64ToBlob(currentLotPhoto.base64Image));
      this.lotPhotoUrl.set(this.sanitizer.bypassSecurityTrustUrl(this.lotPhotoObjectUrl));
      return;
    }

    this.lotPhotoUrl.set(currentLotPhoto.resourceUri);
  }

  /** The sticky header intercepts taps while it's pinned, matching the prototype's scroll behavior. */
  openLotPhotoMenu(): void {
    if (this.isScrolled) {
      return;
    }
    this.isLotPhotoMenuOpen = true;
  }

  get lotPhotoMenuButtons(): VerticalButton[] {
    const buttons: VerticalButton[] = [
      {
        label: "Take Photo",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.triggerLotPhotoCamera(),
        testId: "cm-btn-lot-photo-take"
      },
      {
        label: "Choose from Library",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.triggerLotPhotoLibrary(),
        testId: "cm-btn-lot-photo-library"
      }
    ];

    if (this.currentLotPhoto()) {
      buttons.push({
        label: "Remove Photo",
        severity: "danger",
        styleClass: "bqa-danger-btn",
        action: () => void this.removeLotPhoto(),
        testId: "cm-btn-lot-photo-remove"
      });
    }

    buttons.push({
      label: "Cancel",
      severity: "contrast",
      outlined: true,
      styleClass: "bqa-primary-btn",
      action: () => {
        this.isLotPhotoMenuOpen = false;
      },
      testId: "cm-btn-lot-photo-cancel"
    });

    return buttons;
  }

  triggerLotPhotoCamera(): void {
    this.isLotPhotoMenuOpen = false;
    this.lotPhotoCameraInput?.nativeElement.click();
  }

  triggerLotPhotoLibrary(): void {
    this.isLotPhotoMenuOpen = false;
    this.lotPhotoLibraryInput?.nativeElement.click();
  }

  async onLotPhotoFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";

    if (!file) {
      return;
    }

    try {
      const lotIdentifier = String(this.lotId);
      const media = await this.captureLotPhoto(file, lotIdentifier);

      if (this.currentLotPhoto()) {
        await this.mediaService.softDeleteMediaForParent(lotIdentifier, ImageType.LotPhoto);
      }

      await this.mediaService.bulkAddMedia(media);
      await db.lot.update(this.lotId, { syncStatus: SyncableStatus.Pending });

      await this.loadLotPhoto();
      this.toastService.success("Lot photo updated");
    } catch (error) {
      this.logger.error("Error updating lot photo", error);
      this.toastService.error("Unable to update lot photo. Please try again.");
    }
  }

  async removeLotPhoto(): Promise<void> {
    this.isLotPhotoMenuOpen = false;
    if (!this.currentLotPhoto()) {
      return;
    }

    try {
      await this.mediaService.softDeleteMediaForParent(String(this.lotId), ImageType.LotPhoto);
      await db.lot.update(this.lotId, { syncStatus: SyncableStatus.Pending });
      await this.loadLotPhoto();
      this.toastService.success("Lot photo removed");
    } catch (error) {
      this.logger.error("Error removing lot photo", error);
      this.toastService.error("Unable to remove lot photo. Please try again.");
    }
  }

  /** Waits for compression so the first persisted copy is already the smaller one. */
  private async captureLotPhoto(file: File, parentIdentifier: string): Promise<Media[]> {
    const encoded = await this.imageCompressionPipelineService.encode([file], parentIdentifier, ImageType.LotPhoto);

    let compressedRecords: MediaForCompression[] = encoded;
    this.imageCompressionPipelineService.compress(encoded, async compressed => {
      compressedRecords = compressed;
    });
    await this.imageCompressionPipelineService.waitForCompletion();

    return compressedRecords.map(record => {
      const { file: _file, ...rest } = record;
      return rest;
    });
  }

  private async loadLotJobsData(isInitialLoad = true, isBackgroundRefresh = false): Promise<void> {
    try {
      const jobs = await this.jobService.getAllJobsForLot(this.lotId);
      if (isBackgroundRefresh) {
        await this.imageHydrationService.hydrateByJobIds(this.jobs.map(job => job.id));
      }
      const sortedJobs = jobs.sort((a, b) => a.buildQualityStep - b.buildQualityStep);

      if (sortedJobs.length === 0 && isInitialLoad) {
        if (isBackgroundRefresh) {
          // Don't blank out already-rendered data over what's likely a transient/incomplete read.
          this.logger.error("[LotSummary] Background refresh found no jobs for this lot; keeping existing data", this.lotId);
          return;
        }
        this.jobs = sortedJobs;
        this.alertErrorAndNavigateBack("No jobs found for this lot");
        return;
      }

      this.jobs = sortedJobs;
    } catch (error) {
      this.logger.error("Error loading lot jobs data", error);
      if (!isBackgroundRefresh) {
        this.alertErrorAndNavigateBack("Error loading lot jobs data");
      }
    }
  }

  private async loadLotIssueData(isBackgroundRefresh = false): Promise<void> {
    try {
      const jobIssues = await this.jobIssueService.getLeafJobIssuesForJobs(this.jobs.filter(x => x.isComplete).map(j => j.id));
      const openItems = jobIssues.filter(i => !i.isComplete);

      const today = this.dateUtils.normalizeToLocalDate(new Date()).getTime();
      const dueTimes = openItems
        .filter(issue => !!issue.dueDate)
        .map(issue => this.dateUtils.normalizeToLocalDate(issue.dueDate!).getTime());

      const overdueTimes = dueTimes.filter(dueTime => dueTime < today);

      const millisecondsInDay = 1000 * 60 * 60 * 24;

      this.actionSummary = {
        openItemsCount: openItems.length,
        overdueItemsCount: overdueTimes.length,
        oldestOverdueDays: overdueTimes.length > 0
          ? Math.round((today - Math.min(...overdueTimes)) / millisecondsInDay)
          : null,
        nextDueDate: dueTimes.length > 0 ? new Date(Math.min(...dueTimes)) : null
      };

    } catch (error) {
      this.logger.error("Error loading job statistics", error);
      if (isBackgroundRefresh) {
        return;
      }
      this.alertErrorAndNavigateBack("Error loading job statistics");
    }
  }

  getOverdueSubtext(): string {
    const days = this.actionSummary.oldestOverdueDays;
    if (days === null) {
      return "";
    }
    return `Oldest: ${days} ${days === 1 ? "day" : "days"} overdue`;
  }

  getNextDueSubtext(): string {
    const nextDueDate = this.actionSummary.nextDueDate;
    if (!nextDueDate) {
      return this.actionSummary.openItemsCount > 0 ? "No due date set" : "Nothing open";
    }

    const isToday = this.dateUtils.normalizeToLocalDate(nextDueDate).getTime()
      === this.dateUtils.normalizeToLocalDate(new Date()).getTime();

    return isToday
      ? "Next due: Today"
      : `Next due: ${nextDueDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  }

  goBack = () => {
    if (this.fromAllLots) {
      this.router.navigate(["/cm/all-my-lots/community", this.communityId]);
    } else {
      this.router.navigate(["/cm/schedule"]);
    }
  };

  getBackLabel(): string {
    return this.fromAllLots ? "All Lots" : "Schedule";
  }

  viewOpenItems(): void {
    this.router.navigateWithPersistedState(["cm", "all-my-lots", "community", this.communityId, "lot", this.lotId, "open-items", "list"]);
  }

  viewOverdueItems(): void {
    this.router.navigateWithPersistedState(
      ["cm", "all-my-lots", "community", this.communityId, "lot", this.lotId, "open-items", "list"],
      { queryParams: { due: DueFilterValue.Overdue } }
    );
  }

  viewCompletedItems(): void {
    this.router.navigateWithPersistedState(["cm", "all-my-lots", "community", this.communityId, "lot", this.lotId, "completed"]);
  }

  private alertErrorAndNavigateBack(message: string) {
    this.toastService.error(message);
    this.goBack();
  }

  getClosingDate() {
    return this.lot?.closeOfEscrow
      ? this.dateUtils.convertUtcDateToLocalDateOnly(this.lot.closeOfEscrow)
      : undefined;
  }
}